import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney, fmtDateShort } from "@/lib/fmt";
import { Plus, Trash2, X, Loader2, Globe, Copy, Upload } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";


export default function AdminDirectSales() {
  const [sales, setSales] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [titles, setTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const [s, a, t] = await Promise.all([
      api.get("/admin/direct-sales"),
      api.get("/admin/authors"),
      api.get("/admin/titles"),
    ]);
    setSales(s.data);
    setAuthors(a.data);
    setTitles(t.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this direct sale entry? This cannot be undone.")) return;
    await api.delete(`/admin/direct-sales/${id}`);
    toast.success("Direct sale removed");
    load();
  };

  const totalThisCycle = sales
    .filter((s) => s.period_start >= "2026-01-01")
    .reduce((acc, s) => acc + (s.total_royalty || 0), 0);

  const needsRate = sales.filter((s) => s.needs_rate);

  return (
    <div className="space-y-10" data-testid="admin-direct-sales-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="hh-overline mb-3">Admin · Direct Sales</p>
          <h1 className="font-serif text-4xl text-hh-ink tracking-tight">Healing House direct sales</h1>
          <p className="mt-3 text-hh-ink2 max-w-2xl">
            Log every book sold through the Healing House Publishing website (WordPress) or in-person events.
            These flow into each author&rsquo;s dashboard alongside IngramSpark royalties, clearly badged <em>Direct</em>.
          </p>
        </div>
        <button onClick={() => setOpen(true)} className="hh-btn-primary" data-testid="add-direct-sale-btn">
          <Plus size={16} className="mr-2" /> Add direct sale
        </button>
      </header>

      <div className="hh-card p-6 inline-block">
        <p className="hh-overline mb-2">2026-to-date direct sales royalties</p>
        <p className="font-serif text-3xl text-hh-ink">{fmtMoney(totalThisCycle, "AUD")}</p>
        <p className="text-xs text-hh-ink2 mt-1">{sales.filter((s) => s.period_start >= "2026-01-01").length} entries</p>
      </div>

      <WooCommerceSetup />

      <WcCsvImporter onImported={load} />

      {needsRate.length > 0 && (
        <div className="hh-card p-5 border-l-4 border-l-hh-accent bg-hh-accent/5" data-testid="needs-rate-banner">
          <p className="font-serif text-lg text-hh-ink">
            {needsRate.length} WooCommerce order{needsRate.length === 1 ? "" : "s"} waiting on a royalty rate
          </p>
          <p className="text-xs text-hh-ink2 mt-1 max-w-2xl">
            These came in from WordPress but the matching title doesn&rsquo;t have a <em>Royalty per book</em> set yet.
            Open <strong>Admin → Titles → Edit</strong> for each, set the rate, and the entries below will update automatically when re-ingested.
          </p>
          <ul className="mt-3 text-xs text-hh-ink space-y-1">
            {[...new Set(needsRate.map((s) => `${s.isbn} · ${s.title}`))].map((line) => (
              <li key={line} className="font-mono">· {line}</li>
            ))}
          </ul>
        </div>
      )}

      {loading ? (
        <p className="text-hh-ink2">Loading…</p>
      ) : sales.length === 0 ? (
        <div className="hh-card p-12 text-center">
          <p className="font-serif text-2xl text-hh-ink">No direct sales recorded yet</p>
          <p className="text-sm text-hh-ink2 mt-2 max-w-md mx-auto">
            Click <strong>Add direct sale</strong> above to log each WordPress / in-person sale and the royalty you paid the author.
          </p>
        </div>
      ) : (
        <div className="hh-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-hh-muted/60 border-b border-hh-border text-left">
              <tr>
                <th className="px-6 py-3 hh-overline">Sale date</th>
                <th className="px-6 py-3 hh-overline">Author</th>
                <th className="px-6 py-3 hh-overline">Title</th>
                <th className="px-6 py-3 hh-overline">Units</th>
                <th className="px-6 py-3 hh-overline">Royalty / book</th>
                <th className="px-6 py-3 hh-overline">Total royalty</th>
                <th className="px-6 py-3 hh-overline">Note</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {sales.map((s) => (
                <tr key={s.id} className="border-b border-hh-border last:border-0" data-testid={`direct-sale-${s.id}`}>
                  <td className="px-6 py-3 text-hh-ink2 text-xs">{fmtDateShort(s.period_start)}</td>
                  <td className="px-6 py-3 font-serif text-hh-ink">{s.author_name}</td>
                  <td className="px-6 py-3 text-hh-ink truncate max-w-[260px]" title={s.title}>{s.title}</td>
                  <td className="px-6 py-3 text-hh-ink">{s.units}</td>
                  <td className="px-6 py-3 text-hh-ink2">{fmtMoney(s.royalty_per_book || 0, s.currency)}</td>
                  <td className="px-6 py-3 font-medium text-hh-primary">{fmtMoney(s.total_royalty || 0, s.currency)}</td>
                  <td className="px-6 py-3 text-hh-ink2 text-xs truncate max-w-[200px]" title={s.note}>{s.note || "—"}</td>
                  <td className="px-6 py-3 text-right">
                    <button onClick={() => onDelete(s.id)} className="text-hh-ink2 hover:text-hh-danger" data-testid={`delete-direct-sale-${s.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <DirectSaleForm
          authors={authors}
          titles={titles}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function DirectSaleForm({ authors, titles, onClose, onSaved }) {
  const [authorId, setAuthorId] = useState("");
  const [titleId, setTitleId] = useState("");
  const [saleDate, setSaleDate] = useState(new Date().toISOString().slice(0, 10));
  const [units, setUnits] = useState(1);
  const [royaltyPerBook, setRoyaltyPerBook] = useState("");
  const [currency, setCurrency] = useState("AUD");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const authorTitles = titles.filter((t) => t.author_id === authorId);

  const save = async () => {
    if (!authorId || !titleId || !royaltyPerBook) {
      toast.error("Author, title and royalty are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/direct-sales", {
        author_id: authorId,
        title_id: titleId,
        sale_date: saleDate,
        units: parseInt(units) || 1,
        royalty_per_book: parseFloat(royaltyPerBook) || 0,
        currency,
        note,
      });
      toast.success("Direct sale recorded");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg p-8 shadow-2xl" data-testid="direct-sale-form">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl text-hh-ink">Add direct sale</h2>
          <button onClick={onClose}><X size={18} className="text-hh-ink2" /></button>
        </div>
        <div className="space-y-4">
          <Field label="Author">
            <select value={authorId} onChange={(e) => { setAuthorId(e.target.value); setTitleId(""); }}
                    data-testid="ds-author"
                    className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink">
              <option value="">Select author…</option>
              {authors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Field>
          <Field label="Title / edition">
            <select value={titleId} onChange={(e) => setTitleId(e.target.value)}
                    disabled={!authorId}
                    data-testid="ds-title"
                    className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink disabled:bg-hh-muted/40">
              <option value="">{authorId ? "Select edition…" : "Pick an author first"}</option>
              {authorTitles.map((t) => <option key={t.id} value={t.id}>{t.title} ({t.format})</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Sale date">
              <input type="date" value={saleDate} onChange={(e) => setSaleDate(e.target.value)}
                     data-testid="ds-date"
                     className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink" />
            </Field>
            <Field label="Units sold">
              <input type="number" min="1" value={units} onChange={(e) => setUnits(e.target.value)}
                     data-testid="ds-units"
                     className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Royalty per book">
              <input type="number" step="0.01" value={royaltyPerBook} onChange={(e) => setRoyaltyPerBook(e.target.value)}
                     placeholder="e.g. 7.40"
                     data-testid="ds-royalty"
                     className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink" />
            </Field>
            <Field label="Currency">
              <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                      data-testid="ds-currency"
                      className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink">
                {["AUD", "USD", "GBP", "EUR", "CAD"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Note (optional)">
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
                   placeholder="e.g. WordPress order #1234"
                   data-testid="ds-note"
                   className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink" />
          </Field>
          {royaltyPerBook && units && (
            <div className="text-xs text-hh-ink2 bg-hh-muted/60 border border-hh-border rounded-md px-3 py-2">
              Total royalty: <span className="text-hh-primary font-medium">{currency} {(parseFloat(royaltyPerBook || 0) * parseInt(units || 0)).toFixed(2)}</span>
            </div>
          )}
        </div>
        <div className="mt-8 flex gap-3 justify-end">
          <button onClick={onClose} className="hh-btn-ghost">Cancel</button>
          <button onClick={save} disabled={saving} className="hh-btn-primary disabled:opacity-50" data-testid="ds-save">
            {saving ? <Loader2 size={14} className="animate-spin mr-1.5 inline" /> : null}
            {saving ? "Saving…" : "Save direct sale"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm text-hh-ink2 mb-2">{label}</label>
      {children}
    </div>
  );
}

function WooCommerceSetup() {
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/woocommerce`;
  const copy = async (text, label) => {
    const ok = await copyToClipboard(text);
    if (ok) toast.success(`${label} copied`);
    else toast.error("Could not access clipboard — please copy manually");
  };
  return (
    <div className="hh-card p-6 md:p-7" data-testid="woocommerce-setup">
      <div className="flex items-start gap-3 mb-4">
        <span className="h-9 w-9 rounded-full bg-hh-muted grid place-items-center shrink-0">
          <Globe size={16} strokeWidth={1.5} className="text-hh-primary" />
        </span>
        <div>
          <h3 className="font-serif text-lg text-hh-ink">Auto-log from your WordPress site</h3>
          <p className="text-xs text-hh-ink2 mt-1 max-w-xl">
            Configure a WooCommerce webhook so every completed order flows straight in here as a direct sale. The royalty paid to the author uses each title&rsquo;s <em>Royalty per book</em> (set on Admin → Titles → Edit).
          </p>
        </div>
      </div>
      <ol className="text-sm text-hh-ink2 space-y-3 list-decimal ml-5 mb-5">
        <li>In WordPress admin, go to <strong>WooCommerce → Settings → Advanced → Webhooks</strong> and click <em>Add webhook</em>.</li>
        <li>Topic: <strong>Order updated</strong>. Status: <strong>Active</strong>. API version: <strong>WP REST API Integration v3</strong>.</li>
        <li>Delivery URL: paste the URL below.</li>
        <li>Secret: paste your <code>WOOCOMMERCE_WEBHOOK_SECRET</code> (configured in the backend <code>.env</code>) — both must match exactly.</li>
        <li>On each Title, set <strong>Royalty per book</strong> (Admin → Titles → Edit) so the system knows what to credit the author. Also set the product SKU in WooCommerce to the book&rsquo;s ISBN.</li>
      </ol>
      <div className="bg-hh-muted/60 border border-hh-border rounded-lg p-4 flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-hh-ink2 mb-1">Webhook URL</div>
          <code className="text-xs text-hh-ink font-mono break-all" data-testid="webhook-url">{webhookUrl}</code>
        </div>
        <button onClick={() => copy(webhookUrl, "URL")} className="hh-btn-ghost shrink-0" data-testid="copy-webhook-url">
          <Copy size={14} className="mr-1.5" /> Copy
        </button>
      </div>
      <p className="text-[11px] text-hh-ink2 mt-3">
        Need the secret? Ask your developer or check <code>/app/backend/.env</code> on the server. Duplicate orders (same WC order id + ISBN) are auto-deduplicated.
      </p>
    </div>
  );
}

function WcCsvImporter({ onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);
  const inputRef = useRef(null);

  const run = async (dry) => {
    if (!file) return toast.error("Choose a CSV first.");
    setRunning(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/wc-orders/import?dry_run=${dry ? "true" : "false"}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview({ dry, ...data });
      if (!dry) {
        toast.success(`Imported ${data.imported_count} orders · ${data.skipped_count} skipped · ${data.unmatched_count} unmatched`);
        onImported && onImported();
      } else {
        toast.info(`Preview: ${data.imported_count} would import · ${data.skipped_count} already there · ${data.unmatched_count} unmatched`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="hh-card p-6 space-y-4" data-testid="wc-csv-importer">
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 rounded-full bg-hh-muted grid place-items-center shrink-0">
          <Upload size={15} strokeWidth={1.5} className="text-hh-primary" />
        </span>
        <div className="flex-1">
          <h3 className="font-serif text-lg text-hh-ink">Re-import historical WC orders from CSV</h3>
          <p className="text-xs text-hh-ink2 mt-1 max-w-2xl">
            Use this if any Healing House website orders landed <em>before</em> the buyer&rsquo;s book was in the portal &mdash;
            the live webhook silently drops those. Export the missing orders from WooCommerce and drop the file here.
            <br />
            Required columns: <code>order_id</code>, <code>date</code>, <code>isbn</code>, <code>units</code>.
            Optional: <code>customer</code>, <code>total_aud</code>, <code>format</code>.
            Delimiter can be comma or tab. Re-uploading is safe (idempotent by order_id + ISBN).
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.tsv,.txt"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
          className="text-xs"
          data-testid="wc-csv-file-input"
        />
        <button onClick={() => run(true)} disabled={!file || running} className="hh-btn-outline" data-testid="wc-csv-dry-run">
          {running ? "Working…" : "Preview (dry-run)"}
        </button>
        <button onClick={() => run(false)} disabled={!file || running} className="hh-btn-primary" data-testid="wc-csv-import">
          {running ? "Importing…" : "Import"}
        </button>
      </div>

      {preview && (
        <div className="rounded-lg border border-hh-border bg-hh-cream/40 p-4 text-sm" data-testid="wc-csv-preview-result">
          <p className="font-medium text-hh-ink mb-2">
            {preview.dry ? "Dry run" : "Import complete"} · imported: {preview.imported_count} · skipped: {preview.skipped_count} · unmatched: {preview.unmatched_count}
            {preview.needs_rate_count > 0 && ` · needs royalty rate: ${preview.needs_rate_count}`}
            {preview.error_count > 0 && ` · malformed rows: ${preview.error_count}`}
          </p>
          {preview.errors && preview.errors.length > 0 && (
            <details className="text-xs mt-2" open>
              <summary className="cursor-pointer text-amber-800 font-medium">Malformed rows ({preview.errors.length})</summary>
              <ul className="mt-2 space-y-1 font-mono text-hh-ink">
                {preview.errors.slice(0, 20).map((e) => (
                  <li key={`${e.row}-${e.order_id || ""}`}>· row {e.row}{e.order_id ? ` (order ${e.order_id})` : ""}: {e.reason}</li>
                ))}
              </ul>
            </details>
          )}
          {preview.unmatched && preview.unmatched.length > 0 && (
            <details className="text-xs mt-2" open>
              <summary className="cursor-pointer text-red-700 font-medium">Unmatched ISBNs ({preview.unmatched.length})</summary>
              <ul className="mt-2 space-y-1 font-mono text-hh-ink">
                {preview.unmatched.slice(0, 20).map((u) => (
                  <li key={`${u.order_id}-${u.isbn}`}>· {u.date} · order {u.order_id} · ISBN {u.isbn} × {u.units}</li>
                ))}
                {preview.unmatched.length > 20 && <li className="italic text-hh-ink2">…and {preview.unmatched.length - 20} more</li>}
              </ul>
              <p className="text-hh-ink2 mt-2">Add these titles on <strong>Admin → Titles</strong> with matching ISBNs, then re-run this import.</p>
            </details>
          )}
          {preview.imported && preview.imported.length > 0 && preview.dry && (
            <details className="text-xs mt-2">
              <summary className="cursor-pointer text-emerald-700 font-medium">Would import ({preview.imported.length})</summary>
              <ul className="mt-2 space-y-1 font-mono text-hh-ink">
                {preview.imported.slice(0, 20).map((r) => (
                  <li key={`${r.order_id}-${r.isbn || r.title || r.author_name}`}>· {r.date} · {r.author_name} · order {r.order_id} × {r.units}{r.needs_rate ? " · NEEDS RATE" : ""}</li>
                ))}
                {preview.imported.length > 20 && <li className="italic text-hh-ink2">…and {preview.imported.length - 20} more</li>}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
